import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    QueryList,
    ViewChildren,
    isDevMode,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
} from '@angular/forms';

function getFormArray(size: number): FormArray<FormControl<string | null>> {
    const arr: FormControl<string | null>[] = [];

    for (let i = 0; i < size; i++) {
        arr.push(new FormControl<string | null>(''));
    }

    return new FormArray(arr);
}

@Component({
    selector: 'app-otp-input',
    templateUrl: './otp-input.component.html',
    styleUrls: ['./otp-input.component.scss'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: OtpInputComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: OtpInputComponent,
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpInputComponent implements ControlValueAccessor, Validator {
    private sizeValue = 5;
    private scheduledFocus: number | null = null;

    inputs = getFormArray(this.sizeValue);

    @Input()
    set size(size: number) {
        this.sizeValue = size;
        this.inputs = getFormArray(size);
    }

    @ViewChildren('inputEl') inputEls!: QueryList<ElementRef<HTMLInputElement>>;

    onChange?: (value: string) => void;
    onTouched?: () => void;

    writeValue(value: string): void {
        if (isDevMode() && value && value.length) {
            throw new Error('Otp input is not supposed to be prefilled with data');
        }

        this.inputs.setValue(new Array(this.sizeValue).fill(''));
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.inputs.disable() : this.inputs.enable();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value || value.length < this.sizeValue) {
            return {
                otpInput: 'Value is incorrect',
            };
        }
        return null;
    }

    public clear() {
        this.inputs.setValue(new Array(this.sizeValue).fill(''));
        this.scheduledFocus = 0;
        this.focusInput(0);
    }

    handleKeyDown(e: KeyboardEvent, idx: number) {
        if ((e.key === 'Backspace' || e.key === 'Delete') && idx > 0) {
            this.scheduledFocus = idx - 1;
        }
    }

    handleInput() {
        this.updateWiredValue();
        if (this.scheduledFocus != null) {
            this.focusInput(this.scheduledFocus);
            this.scheduledFocus = null;
        }
    }

    handleKeyPress(e: KeyboardEvent, idx: number) {
        const isDigit = /\d/.test(e.key);

        if (e.key === 'v' && e.metaKey) return true;

        if (isDigit && idx + 1 < this.sizeValue) {
            this.scheduledFocus = idx + 1;
        }

        if (isDigit && this.inputs.controls[idx].value) {
            this.inputs.controls[idx].setValue('');
        }

        return isDigit;
    }

    handlePaste(e: ClipboardEvent, idx: number) {
        e.preventDefault();

        if (idx !== 0) return;

        const pasteData = e.clipboardData?.getData('text');
        const regex = new RegExp(`\\d{${this.sizeValue}}`);

        if (!pasteData || !regex.test(pasteData)) return;

        for (let i = 0; i < pasteData.length; i++) {
            this.inputs.controls[i].setValue(pasteData[i]);
        }

        this.focusInput(this.inputEls.length - 1);
        this.updateWiredValue();
        this.onTouched?.();
    }

    handleFocus(e: FocusEvent) {
        (e.target as HTMLInputElement).select();
    }

    private focusInput(idx: number) {
        setTimeout(() => {
            const inputEl = this.inputEls.toArray()[idx];
            if (inputEl) {
                inputEl.nativeElement.focus();
            }
        });
    }

    private updateWiredValue() {
        setTimeout(() => {
            this.onChange?.(this.inputs.value.join(''));
        });
    }
}
